import _has from 'lodash/has';
import _set from 'lodash/set';
import _get from 'lodash/get';
import { ImagePropsField } from '@src/slide/domain/ui-model/element-modeles/property/image-operation/constant/props-value';
import isUndefined from 'lodash/isUndefined';

// 黑白阈值的单位转换
export const BI_LEVEL_COEFFICIENT = 1000;
export const LUMINANCE_COEFFICIENT = 1000;

// 冲蚀效果的亮度、对比度的固定值
export const LUMINANCE_BRIGHTNESS = 70000;
export const LUMINANCE_CONTRAST = -70000;


// 迁移自pictureConvert.ts
export default class ImageEffectConvert {
    /**
     * SDK to UI，转换图片相关的数据，锐化/柔化、亮度、对比度、饱和度、色温
     * @param sdkData 转换到的目标数据
     * @param uiData  需要转换的UI数据，UIModel的子集
     */
    public static convertImageBlipDataSdkToUi(blip: any, uiData: any) {
        if (blip) {
            // 必有的属性
            this.setImageBlipUiData(uiData, blip, 'sharpenSoften', ImagePropsField.ClARITY.coefficient, ImagePropsField.ClARITY.defaultValue);
            this.setImageBlipUiData(uiData, blip, 'brightness', ImagePropsField.BRIGHTNESS.coefficient, ImagePropsField.BRIGHTNESS.defaultValue);
            this.setImageBlipUiData(uiData, blip, 'contrast', ImagePropsField.CONTRAST.coefficient, ImagePropsField.CONTRAST.defaultValue);
            this.setImageBlipUiData(uiData, blip, 'saturate', ImagePropsField.SATURATE.coefficient, ImagePropsField.SATURATE.defaultValue);
            this.setImageBlipUiData(uiData, blip, 'colorTemperature', ImagePropsField.COLORTEMPERATURE.coefficient, ImagePropsField.COLORTEMPERATURE.defaultValue);
            if (blip?.grayscl) {
                _set(uiData.props.style, 'image.grayscl', blip.grayscl);
            } else {
                _set(uiData.props.style, 'image.grayscl', false);
            }
            // 非必有属性
            if (blip?.biLevel || blip?.biLevel === 0) {
                _set(uiData.props.style, 'image.biLevel', blip.biLevel / BI_LEVEL_COEFFICIENT);
            } else {
                _set(uiData.props.style, 'image.biLevel', undefined);
            }

            if (blip?.duotone) {
                _set(uiData.props.style, 'image.duotone', blip.duotone);
            } else {
                _set(uiData.props.style, 'image.duotone', undefined);
            }

            if (blip?.luminance) {
                const { brightness, contrast } = blip.luminance;
                if (brightness === LUMINANCE_BRIGHTNESS && contrast === LUMINANCE_CONTRAST) {
                    _set(uiData.props.style, 'image.luminance', {
                        brightness: brightness / LUMINANCE_COEFFICIENT,
                        contrast: contrast / LUMINANCE_COEFFICIENT,
                    });
                } else {
                    _set(uiData.props.style, 'image.luminance', undefined);
                }
            } else {
                _set(uiData.props.style, 'image.luminance', undefined);
            }
        } else {
            const image = {
                sharpenSoften: ImagePropsField.ClARITY.defaultValue,
                brightness: ImagePropsField.BRIGHTNESS.defaultValue,
                contrast: ImagePropsField.CONTRAST.defaultValue,
                saturate: ImagePropsField.SATURATE.defaultValue,
                colorTemperature: ImagePropsField.COLORTEMPERATURE.defaultValue,
            };
            _set(uiData, 'props.style.image', image);
        }

        // 设置透明度
        if (_has(blip, 'alphaModFix')) {
            _set(uiData, 'props.style.alpha', blip.alphaModFix / 100000);
        } else {
            _set(uiData, 'props.style.alpha', 1);
        }
    }

    /**
     * UI to SDK，转换图片校正/图片颜色数据
     */
    public static convertImageEffectDataUiToSdk(uiData: any, sdkData: any, sdkPath = '') {
        if (uiData?.props?.style?.image) {
            const blipExt = uiData.props.style.image;
            this.setImageBlipSdkData(sdkData, blipExt, 'sharpenSoften', ImagePropsField.ClARITY.coefficient, sdkPath);
            this.setImageBlipSdkData(sdkData, blipExt, 'brightness', ImagePropsField.BRIGHTNESS.coefficient, sdkPath);
            this.setImageBlipSdkData(sdkData, blipExt, 'contrast', ImagePropsField.CONTRAST.coefficient, sdkPath);
            this.setImageBlipSdkData(sdkData, blipExt, 'saturate', ImagePropsField.SATURATE.coefficient, sdkPath);
            this.setImageBlipSdkData(sdkData, blipExt, 'colorTemperature', ImagePropsField.COLORTEMPERATURE.coefficient, sdkPath);
            if (_has(blipExt, 'grayscl')) {
                this.setImageBlip(sdkData, 'grayscl', blipExt.grayscl, sdkPath);
            }
            let biLevel;
            if (blipExt?.biLevel) {
                biLevel = blipExt.biLevel * BI_LEVEL_COEFFICIENT;
            } else {
                if (blipExt?.biLevel === 0) {
                    biLevel = 0;
                } else {
                    biLevel = null;
                }
            }
            this.setImageBlip(sdkData, 'biLevel',  biLevel, sdkPath);
            this.setImageBlip(sdkData, 'duotone',  _has(blipExt, 'duotone') ? blipExt.duotone : null, sdkPath);

            if (_get(blipExt, 'luminance')) {
                const brightness = blipExt.luminance.brightness * LUMINANCE_COEFFICIENT;
                const contrast = blipExt.luminance.contrast * LUMINANCE_COEFFICIENT;
                this.setImageBlip(sdkData, 'luminance',  { brightness, contrast }, sdkPath);
            } else {
                this.setImageBlip(sdkData, 'luminance',  null, sdkPath);
            }
        }

        // 透明度
        if (_get(uiData, 'props.style.alpha')) {
            this.setImageBlip(sdkData, 'alphaModFix', uiData.props.style.alpha * 100000, sdkPath);
        }
    }

    /**
     * SDK to UI，提取重复调用的方法
     * @param uiData 需要转换的UI数据，UIModel的子集
     * @param coefficient 除以的系数
     * @param defaultValue 默认值
     */
    private static setImageBlipUiData(
        uiData: any,
        blip: any,
        key: string,
        coefficient = 1,
        defaultValue = 1,
    ) {
        if (_has(blip, key)) {
            _set(uiData, `props.style.image.${key}`, blip[key] / coefficient);
        } else {
            _set(uiData, `props.style.image.${key}`, defaultValue);
        }
    }

    /**
     * UI to SDK，提取重复调用的方法
     * @param sdkData 转换到的目标数据
     * @param coefficient 乘以的系数
     */
    private static setImageBlipSdkData(
        sdkData: any,
        blipExt: any,
        key: string,
        coefficient: number,
        sdkPath?: string,
    ) {
        if (_has(blipExt, key)) {
            const path = sdkPath ? `${sdkPath}.image.blip.${key}` : `image.blip.${key}`;
            _set(sdkData, path, _get(blipExt, key) * coefficient);
        }
    }

    private static setImageBlip(sdkData, path: string, value, sdkPath?: string) {
        path = sdkPath ? `${sdkPath}.image.blip.${path}` : `image.blip.${path}`;
        _set(sdkData,  path, value);
    }
}
